import { apiErrorMessage } from '@agos/api-client'
import {
  getGetSuperAdminFranchisesQueryKey,
  usePatchSuperAdminFranchisesFranchiseId,
} from '@agos/api-client/platform'
import { T, tt } from '@agos/shared'
import { BUTTON_PRIMARY, cn, toast } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Globe } from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  customerDomainNote,
  customerDomainOf,
  primaryDomainChanged,
  primaryDomainPayload,
  registeredDomainsOf,
} from './customer-domain'

/**
 * *Doména zákazníckej stránky* - `FRONTEND.md` §9.702, owner O84, `BACKEND.md` §10A.359.
 *
 * **Its own save rather than the franchise card's**: the card is one `PATCH` of `franchises` columns and its
 * draft is held to `EDITABLE` by `FranchiseColumnsAgreeTest`; this chooses among `franchise_domains` rows, a
 * different table, and the server takes it as its own key. A select of the registered domains only - see
 * `customer-domain.ts` for why free text would offer a link that opens nothing.
 */
export function CustomerDomainSection({
  franchiseId,
  franchise,
}: {
  franchiseId: number
  franchise: Record<string, unknown> | null
}) {
  const queryClient = useQueryClient()
  const patch = usePatchSuperAdminFranchisesFranchiseId()
  const domains = useMemo(() => registeredDomainsOf(franchise), [franchise])
  const current = customerDomainOf(domains)
  const [chosen, setChosen] = useState<string>('')
  // The select shows what the operator picked, else what is stored - so a refetch after saving reads back.
  const shown = chosen !== '' ? chosen : (current ?? '')

  const save = () => {
    if (!primaryDomainChanged(shown, domains)) return
    void patch
      .mutateAsync({ franchiseId, data: primaryDomainPayload(shown) })
      .then(async () => {
        await queryClient.invalidateQueries({ queryKey: getGetSuperAdminFranchisesQueryKey() })
        setChosen('')
        toast({ title: tt('Doména zákazníckej stránky uložená') })
      })
      .catch((failure: unknown) =>
        toast({
          title: apiErrorMessage(failure, tt('Doménu sa nepodarilo uložiť')),
          variant: 'destructive',
        }),
      )
  }

  return (
    <section
      className="bg-card border border-border rounded-2xl overflow-hidden"
      data-customer-domain={current ?? ''}
    >
      <div className="px-5 py-3 border-b border-border flex items-center gap-2">
        <Globe className="w-4 h-4 text-muted-foreground" />
        <h2 className="font-bold text-foreground text-sm">
          <T>Doména zákazníckej stránky</T>
        </h2>
      </div>
      <div className="p-5 space-y-3">
        <p className="text-[11px] text-muted-foreground">
          <T>
            Na túto doménu vedú odkazy v e-mailoch zákazníkom (obnova hesla, pätička), odkaz na odporúčanie
            priateľa a výber pobočky. Ponúkajú sa len domény zaregistrované pre túto franšízu - inú treba najprv
            pridať (franchise-setup.sh, Domény), inak by na nej odkaz neotvoril nič. Po zmene ju vystavte na proxy
            znova, aby smerovala na zákaznícku stránku.
          </T>
        </p>
        <p
          className={cn(
            'text-xs',
            domains.length > 0 && current === null ? 'text-amber-600 font-semibold' : 'text-foreground',
          )}
          data-customer-domain-note
        >
          {customerDomainNote(domains)}
        </p>
        {domains.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={shown}
              onChange={(event) => setChosen(event.target.value)}
              aria-label={tt('Doména zákazníckej stránky')}
              data-customer-domain-select
              className="min-h-[40px] rounded-xl border border-border bg-background px-3 text-sm"
            >
              {current === null && <option value="">{tt('- zvoľte doménu -')}</option>}
              {domains.map((one) => (
                <option key={one.domain} value={one.domain}>
                  {one.domain}
                </option>
              ))}
            </select>
            <button
              type="button"
              data-customer-domain-save
              onClick={save}
              disabled={patch.isPending || !primaryDomainChanged(shown, domains)}
              className={cn('min-h-[40px] px-4 rounded-xl text-sm disabled:opacity-50', BUTTON_PRIMARY)}
            >
              <T>Uložiť doménu</T>
            </button>
          </div>
        )}
      </div>
    </section>
  )
}
